/**
 * SND-17518 — "incl. VAT / excl. VAT" price switch (top bar).
 *
 * Prices are rendered gross by Shopify. When the shopper picks "excl. VAT" this
 * module rewrites every price TEXT it knows about (product page, cards,
 * search panel, recently viewed…) to gross ÷ rate, where the rate is the
 * destination-country multiplier from snippets/snd-vat-rate.liquid (passed on
 * <snd-topbar data-rate>). Cart and checkout are NOT touched: what is charged
 * stays what Shopify charges, and tax-exempt B2B accounts get their 0 % at
 * checkout as before.
 *
 * Default: a logged-in customer flagged "don't collect VAT" (customer.tax_exempt)
 * starts on "excl." unless they switched by hand before; everyone else on
 * "incl.". The choice persists in localStorage.
 *
 * Only leaf elements are rewritten (a span with the money text and no element
 * children). The original text is kept in data-snd-vat-orig; when the theme
 * re-renders a price (variant change, section morph) the new text is picked up
 * as the new original. Anything inside [data-snd-vat-skip] is left alone.
 */
(function () {
  const KEY = 'snd:vat';
  const MANUAL_KEY = 'snd:vat:manual';
  const SELECTOR = 'span.price, .price-item, .compare-at-price, [data-snd-vat-price]';
  const bar = document.querySelector('snd-topbar');
  if (!bar) return;

  const rate = parseFloat(bar.dataset.rate || '1') || 1;
  const format = bar.dataset.moneyFormat || '';
  const exempt = bar.dataset.exempt === '1';

  const comma = /with_comma_separator/.test(format);
  const space = /with_space_separator/.test(format);
  const apostrophe = /with_apostrophe_separator/.test(format);
  const noDecimals = /no_decimals/.test(format);
  const dec = comma || space ? ',' : '.';
  const thou = comma ? '.' : space ? ' ' : apostrophe ? "'" : ',';
  const esc = (c) => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const NUM = new RegExp(
    '\\d{1,3}(?:[' + esc(thou) + '\\u00a0\\u202f ]\\d{3})+(?:' + esc(dec) + '\\d{1,2})?|\\d+(?:' + esc(dec) + '\\d{1,2})?'
  );

  const read = (k) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  };
  const write = (k, v) => {
    try {
      localStorage.setItem(k, v);
    } catch {}
  };

  let mode = read(KEY) === 'excl' ? 'excl' : 'incl';
  if (exempt && read(MANUAL_KEY) !== '1') mode = 'excl';

  function parse(text) {
    const cleaned = text.replace(new RegExp('[' + esc(thou) + '\\u00a0\\u202f ]', 'g'), '').replace(dec, '.');
    return parseFloat(cleaned);
  }

  function formatNumber(value) {
    const fixed = value.toFixed(noDecimals ? 0 : 2);
    const [int, frac] = fixed.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thou);
    return frac ? grouped + dec + frac : grouped;
  }

  function convert(el) {
    if (!(el instanceof HTMLElement) || el.children.length > 0) return;
    if (el.closest('[data-snd-vat-skip]')) return;
    const current = el.textContent;
    if (!current || !NUM.test(current)) return;
    // a text we did not write is the (new) original
    if (current !== el.dataset.sndVatText) el.dataset.sndVatOrig = current;
    const orig = el.dataset.sndVatOrig ?? current;
    let target = orig;
    if (mode === 'excl') {
      target = orig.replace(NUM, (m) => {
        const n = parse(m);
        return Number.isFinite(n) ? formatNumber(n / rate) : m;
      });
    }
    el.dataset.sndVatText = target;
    if (el.textContent !== target) el.textContent = target;
  }

  let scheduled = false;
  function applyAll() {
    scheduled = false;
    document.documentElement.setAttribute('data-snd-vat', mode);
    document.querySelectorAll(SELECTOR).forEach(convert);
    document.querySelectorAll('[data-snd-vat-set]').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.sndVatSet === mode ? 'true' : 'false');
    });
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(applyAll);
  }

  /* SND-17518: "buying for a business?" — once, on a manual switch to excl.,
     only where the bar says so (outside Lithuania). */
  const PROMPT_KEY = 'snd:vat:b2b-prompt-seen';
  function maybePrompt() {
    if (bar.dataset.b2bPrompt !== '1' || read(PROMPT_KEY) === '1') return;
    const dialog = bar.querySelector('[data-snd-b2b-prompt]');
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    write(PROMPT_KEY, '1');
    dialog.addEventListener('close', () => document.documentElement.removeAttribute('data-snd-b2b-open'), { once: true });
    document.documentElement.setAttribute('data-snd-b2b-open', '');
    dialog.showModal();
  }

  function set(next, manual) {
    mode = next === 'excl' ? 'excl' : 'incl';
    write(KEY, mode);
    if (manual) write(MANUAL_KEY, '1');
    applyAll();
    document.dispatchEvent(new CustomEvent('snd:vat', { detail: { mode } }));
    if (manual && mode === 'excl') maybePrompt();
  }

  document.addEventListener('click', (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-snd-vat-set]') : null;
    if (!btn) return;
    event.preventDefault();
    set(btn.dataset.sndVatSet, true);
  });

  window.SndVat = {
    get mode() {
      return mode;
    },
    set: (m) => set(m, true),
    refresh: applyAll,
  };

  const start = () => {
    applyAll();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, characterData: true });
  };
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
